import { Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toIsoDate } from '@/lib/calendar';
import type { GraphConfig } from '@/api/targetGraphConfigs';

interface Props {
    config: GraphConfig;
    onChange: (next: GraphConfig) => void;
    onSave: () => void;
    onReset: () => void;
    saving: boolean;
    resetting: boolean;
    dirty: boolean;
    hasSavedConfig: boolean;
}

export function GraphConfigPanel({
    config, onChange, onSave, onReset, saving, resetting, dirty, hasSavedConfig,
}: Props) {
    const update = <K extends keyof GraphConfig>(key: K, value: GraphConfig[K]) => {
        onChange({ ...config, [key]: value });
    };

    return (
        <div className="space-y-4">
            <fieldset className="space-y-2">
                <legend className="text-xs font-bold text-text-muted uppercase tracking-wider">Chart</legend>
                <Row label="Type">
                    <select
                        value={config.chartType}
                        onChange={(e) => update('chartType', e.target.value as GraphConfig['chartType'])}
                        className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                    >
                        <option value="line">Line</option>
                        <option value="bar">Bar</option>
                    </select>
                </Row>
                <Row label="Aggregation">
                    <select
                        value={config.aggregation}
                        onChange={(e) => update('aggregation', e.target.value as GraphConfig['aggregation'])}
                        className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                    >
                        <option value="raw">Raw (per session)</option>
                        <option value="daily_avg">Daily average</option>
                        <option value="weekly_avg">Weekly average</option>
                        <option value="cumulative">Cumulative</option>
                    </select>
                </Row>
            </fieldset>

            <fieldset className="space-y-2">
                <legend className="text-xs font-bold text-text-muted uppercase tracking-wider">Date range</legend>
                <Row label="Window">
                    <select
                        value={config.dateRange}
                        onChange={(e) => update('dateRange', e.target.value as GraphConfig['dateRange'])}
                        className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                    >
                        <option value="30d">Last 30 days</option>
                        <option value="60d">Last 60 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="all">All data</option>
                        <option value="custom">Custom…</option>
                    </select>
                </Row>
                {config.dateRange === 'custom' && (
                    <>
                        <Row label="From">
                            <input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={config.customStart ?? ''}
                                onChange={(e) => update('customStart', e.target.value || null)}
                                className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                            />
                        </Row>
                        <Row label="To">
                            <input
                                type="date"
                                min={config.customStart || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={config.customEnd ?? ''}
                                onChange={(e) => update('customEnd', e.target.value || null)}
                                className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                            />
                        </Row>
                    </>
                )}
            </fieldset>

            <fieldset className="space-y-2">
                <legend className="text-xs font-bold text-text-muted uppercase tracking-wider">Overlays</legend>
                <label className="flex items-center gap-2 text-xs text-text cursor-pointer">
                    <input
                        type="checkbox"
                        checked={config.showCriterion}
                        onChange={(e) => update('showCriterion', e.target.checked)}
                    />
                    Show mastery criterion
                </label>
                <label className="flex items-center gap-2 text-xs text-text cursor-pointer">
                    <input
                        type="checkbox"
                        checked={config.showTrendline}
                        onChange={(e) => update('showTrendline', e.target.checked)}
                    />
                    Show trendline
                </label>
            </fieldset>

            <fieldset className="space-y-2">
                <legend className="text-xs font-bold text-text-muted uppercase tracking-wider">Y-axis bounds</legend>
                <Row label="Min">
                    <input
                        type="number"
                        value={config.yAxisMin ?? ''}
                        placeholder="auto"
                        onChange={(e) => update('yAxisMin', e.target.value === '' ? null : Number(e.target.value))}
                        className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                    />
                </Row>
                <Row label="Max">
                    <input
                        type="number"
                        value={config.yAxisMax ?? ''}
                        placeholder="auto"
                        onChange={(e) => update('yAxisMax', e.target.value === '' ? null : Number(e.target.value))}
                        className="w-full border border-border rounded-md px-2 py-1 text-xs bg-surface"
                    />
                </Row>
            </fieldset>

            <div className="flex flex-col gap-2 pt-2 border-t border-border">
                <Button
                    variant="primary"
                    size="sm"
                    onClick={onSave}
                    disabled={saving || !dirty}
                    title={dirty ? 'Save these settings as the default for this target' : 'No changes to save'}
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save as default
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onReset}
                    disabled={resetting || !hasSavedConfig}
                    title={hasSavedConfig ? 'Forget saved default for this target' : 'No saved default to reset'}
                >
                    {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Reset to default
                </Button>
            </div>
        </div>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="flex items-center gap-2 text-xs text-text-muted">
            <span className="w-20 shrink-0">{label}</span>
            <div className="flex-1">{children}</div>
        </label>
    );
}
